import { useMemo, useState } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import RentalService from '../../../services/RentalService';
import { Rental, RentalStatus } from '../../../services/models/rental';
import { Lock } from '../../../services/models/assets';
import Timeline from '../../../components/Timeline/Timeline';
import RentalTooltip from './RentalTooltip';
import { getRentalStatusInfo } from '../utils/rentalUtils';
import { NO_LOCK_KEY, RentalLockItem, expandRentalsToLockItems, findLockConflictRanges } from '../utils/rentalTimelineUtils';
import { generateDebugRentals } from '../utils/debugRentals';

const IS_DEV = process.env.NODE_ENV === 'development';

const renterName = (item: RentalLockItem) =>
  `${item.rental.user.firstName} ${item.rental.user.lastName}`;

const rentalLockPositions = (rental: Rental): number[] =>
  (rental.equipments ?? [])
    .map((equipment) => equipment.lock?.position)
    .filter((position): position is number => position != null);

// The renter's name is shown on ONE bar of the rental only — the topmost lock —
// so a multi-lock rental doesn't repeat it on every row.
const isLabeledItem = (item: RentalLockItem) => {
  const positions = rentalLockPositions(item.rental);
  return positions.length === 0 || item.lockPosition === Math.min(...positions);
};

// The label overflows the bar instead of being ellipsized, so the lane packing
// must reserve the label's real footprint: ~7px per glyph (typo-small semibold)
// plus the bar/text padding. Estimated, not measured — close enough to keep
// neighbouring labels from colliding. Unlabeled bars only reserve a stub.
const barFootprintPx = (item: RentalLockItem) =>
  isLabeledItem(item) ? Math.max(64, renterName(item).length * 7 + 24) : 16;

interface RentalLockTimelineProps {
  stationId: number;
  /** Station locks, one fixed timeline row each (sorted by position). */
  locks: Lock[];
  onRentalClick: (rental: Rental) => void;
}

// One row per lock: every lock reads as a lane (empty = free), a multi-lock
// rental appears as vertically aligned bars tied together on hover, and
// consecutive bars on a row show who had the lock before/after — which is how
// staff verify a previous rental before capturing its payment.
const RentalLockTimeline: React.FC<RentalLockTimelineProps> = ({ stationId, locks, onRentalClick }) => {
  const [tooltip, setTooltip] = useState<{ item: RentalLockItem; x: number; y: number } | null>(null);
  const [hoveredRentalId, setHoveredRentalId] = useState<number | null>(null);
  const [range, setRange] = useState<{ start: Date; end: Date } | null>(null);
  const [showDebugData, setShowDebugData] = useState(false);

  const { data } = useQuery({
    queryKey: ['rentals', stationId, 'range', range?.start.toISOString(), range?.end.toISOString()],
    queryFn: () => RentalService.getRentalsRange(stationId, range!.start.toISOString(), range!.end.toISOString()),
    enabled: range !== null,
    placeholderData: keepPreviousData,
  });

  // Dev-only fake schedule merged on top of the real rentals (never in prod
  // builds: CRA sets NODE_ENV=production there, so the toggle never shows).
  const debugRentals = useMemo(
    () => (IS_DEV && showDebugData ? generateDebugRentals(locks, new Date()) : []),
    [showDebugData, locks],
  );

  const items = useMemo(
    () => expandRentalsToLockItems([...(data?.rentals ?? []), ...debugRentals]),
    [data?.rentals, debugRentals],
  );

  const conflictRanges = useMemo(() => findLockConflictRanges(items), [items]);

  const laneGroups = useMemo(() => {
    const groups: { key: number; label: string }[] = [...locks]
      .sort((a, b) => a.position - b.position)
      .map((lock) => ({ key: lock.position, label: `L${lock.position + 1}` }));
    if (items.some((item) => item.lockPosition === NO_LOCK_KEY)) {
      groups.push({ key: NO_LOCK_KEY, label: 'Sans lock' });
    }
    return groups;
  }, [locks, items]);

  return (
    <Timeline<RentalLockItem>
      items={items}
      defaultRange="3d"
      urlSync
      onRangeChange={(start, end) => setRange({ start, end })}
      title="Réservations"
      icon={<CalendarDaysIcon className="w-5 h-5 text-primary-500" />}
      legend={IS_DEV ? (
        <button
          type="button"
          onClick={() => setShowDebugData((value) => !value)}
          className={`self-start px-2 py-0.5 rounded-md typo-caption !font-semibold transition-colors ${
            showDebugData
              ? 'bg-primary-100 text-primary-700'
              : 'bg-neutral-5 text-neutral-40 hover:text-neutral'
          }`}
        >
          {showDebugData ? 'Données de test : ON' : 'Données de test'}
        </button>
      ) : undefined}
      laneGroups={laneGroups}
      conflictRanges={conflictRanges}
      // Tie the bars of a multi-lock rental together with a vertical string in
      // the rental's status color, softened; closed rentals fade further.
      connectKey={(item) => (new Set(rentalLockPositions(item.rental)).size > 1 ? item.rental.id : null)}
      connectorColor={(item) => getRentalStatusInfo(item.rental.rentalStatus).connectorColor}
      connectorClassName={(item) =>
        item.rental.rentalStatus === RentalStatus.CLOSED ? 'opacity-20' : 'opacity-40'
      }
      laneGroupKey={(item) => item.lockPosition}
      laneMinBarPx={barFootprintPx}
      barClassName={(item) =>
        `${getRentalStatusInfo(item.rental.rentalStatus).barClassName}${
          item.rental.id === hoveredRentalId ? ' ring-2 ring-primary-400 ring-offset-1' : ''
        }`
      }
      renderBarContent={(item) =>
        isLabeledItem(item) ? (
          <span className="typo-small !font-semibold text-neutral whitespace-nowrap px-1">
            {renterName(item)}
          </span>
        ) : null
      }
      onBarClick={(item) => onRentalClick(item.rental)}
      onBarHover={(item, x, y) => {
        setTooltip({ item, x, y });
        setHoveredRentalId(item.rental.id);
      }}
      onBarLeave={() => {
        setTooltip(null);
        setHoveredRentalId(null);
      }}
    >
      {tooltip && <RentalTooltip rental={tooltip.item.rental} x={tooltip.x} y={tooltip.y} />}
    </Timeline>
  );
};

export default RentalLockTimeline;
